import { Stack, SxProps, TextField, Theme } from '@mui/material'
import _ from 'lodash'
import { ChangeEvent } from 'react'
import { Controller, ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form'

import Tooltip from 'components/Tooltip'
import { inputWidth } from 'providers/material-ui/theme/constants'
import { isNumericString } from 'utils/string'
import { createHelperText } from '../TextField'
import { NumberFieldProps } from './types'

const parseValue = (fieldValue: null | number | string | undefined): string => {
  const EMPTY_VALUE: string = ''

  if (_.isNil(fieldValue)) {
    return EMPTY_VALUE
  }

  if (typeof fieldValue === 'number' && _.isNumber(fieldValue)) {
    return fieldValue.toString()
  } else if (
    typeof fieldValue === 'string' &&
    isNumericString(fieldValue) &&
    fieldValue.length > 0
  ) {
    return fieldValue
  } else return EMPTY_VALUE
}

export const NumberField = <T extends FieldValues>({
  control,
  fixedHelperText,
  helperText,
  name,
  tooltip: tooltipProps,
  type = 'integer',
  useTooltipForErrors,
  ...textFieldProps
}: NumberFieldProps<T>): JSX.Element => {
  const handleChange = <T extends FieldValues>(
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: ControllerRenderProps<T, FieldPath<T>>,
  ) => void field.onChange(Number(event.target.value) || 0)

  const calculatedWidth: number | string = inputWidth[textFieldProps.width ?? 'sm']

  const containerSxProps: SxProps<Theme> = {
    minWidth: calculatedWidth,
  }

  const inputSxProps: SxProps<Theme> = {
    minWidth: calculatedWidth,
    width: calculatedWidth,
  }

  return (
    <Controller
      control={control}
      name={name as FieldPath<T>}
      render={({ field, field: { value }, fieldState: { error } }) => {
        const hasError: boolean = Boolean(error)
        const errorMessage: string = error?.message || ''

        return (
          <Stack
            direction='row'
            sx={containerSxProps}
          >
            <TextField
              {...field}
              {...textFieldProps}
              error={hasError}
              helperText={createHelperText<T>({
                errorMessage,
                fixedHelperText,
                hasError,
                helperText,
                useTooltipForErrors,
              })}
              id={name}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: type === 'integer' ? 1 : 0.01 }}
              onChange={event => handleChange(event, field)}
              sx={{
                ...inputSxProps,
                ...textFieldProps.sx,
              }}
              type='number'
              value={parseValue(value)}
            />

            {tooltipProps && <Tooltip {...tooltipProps} />}

            {hasError && useTooltipForErrors && (
              <Tooltip
                content={errorMessage}
                variant='error'
              />
            )}
          </Stack>
        )
      }}
    />
  )
}
